/**
 * LES FORMATS D'UNE PAGE DE CONTEXTE.
 *
 * CE QU'ILS REMPLACENT. Le contenu d'une page de contexte se réglait par trois
 * cases à cocher indépendantes — « seulement le média », « afficher du texte »,
 * « avec audio » — qui décrivaient le rendu CHAMP PAR CHAMP sans jamais nommer
 * la page obtenue. Rien n'empêchait de les combiner en formes qui n'existent
 * pas : les décocher toutes les trois donnait un média sur la moitié gauche de
 * l'écran et une moitié droite vide, et cela ne se voyait qu'une fois projeté.
 *
 * Un format est une FORME D'ÉCRAN ENTIÈRE, choisie sur croquis. C'est le
 * vocabulaire que le module territoire emploie déjà pour ses étapes (cf.
 * `stepKinds.js`) ; il n'y avait pas de raison qu'une page de contexte — 187
 * des 240 pages de la base — se compose autrement.
 *
 * ILS N'ONT AUCUN RÉGLAGE : ni côté du média, ni proportion, ni alignement. La
 * personnalisation est le choix de la forme. Cinq formes dessinées valent mieux
 * qu'une forme molle et douze curseurs, parce qu'une page ratée est une page
 * ratée devant une salle — et parce qu'un réglage se règle une fois et se
 * maintient toujours.
 *
 * LES EMPLACEMENTS NE SONT PAS DES RÉGLAGES. Un format déclare les
 * emplacements qu'il possède ; un emplacement porte quelque chose, ou ne porte
 * rien. « Afficher le texte » n'était pas une question à poser à un auteur qui
 * vient d'écrire le texte, ni à celui qui n'a rien écrit.
 */

export const CONTEXT_LAYOUT_IDS = ["slide", "split", "text", "cover", "compare"];

export const DEFAULT_CONTEXT_LAYOUT = "slide";

/**
 * Ce que chaque format possède. Un emplacement absent n'est ni rendu, ni
 * proposé à l'auteur, ni conservé à la lecture : changer de format ne laisse
 * pas derrière soi un texte invisible qui ressortira trois mois plus tard.
 *
 * `media` vaut `"photo"` là où une vidéo n'aurait pas de sens : en plein cadre,
 * le visuel EST le décor de la page, posé sous le texte et sous le bouton —
 * une vidéo y serait une image de fond animée que personne ne peut mettre en
 * pause, et le format n'a nulle part où loger ses commandes.
 *
 * `items` est le nombre MAXIMAL de visuels comparés (0 = le format n'en a pas).
 */
export const CONTEXT_LAYOUT_SLOTS = {
  slide: { media: true, text: false, audio: true, items: 0 },
  split: { media: true, text: true, audio: true, items: 0 },
  text: { media: false, text: true, audio: true, items: 0 },
  cover: { media: "photo", text: true, audio: true, items: 0 },
  compare: { media: false, text: true, audio: false, items: 3 },
};

/** Deux visuels au minimum : à un seul, ce n'est plus une comparaison. */
export const COMPARE_MIN_ITEMS = 2;

export const CONTEXT_LAYOUT_INFO = {
  slide: {
    label: "Diapo",
    hint: "Un visuel préparé ailleurs, projeté entier. La forme la plus courante.",
    textLabel: null,
  },
  split: {
    label: "Média et panneau",
    hint: "Une image ou une vidéo d'un côté, le texte et le son de l'autre.",
    textLabel: "Texte du panneau",
  },
  text: {
    label: "Texte seul",
    hint: "Une adresse à la salle, sans visuel. Le fond de la page reste visible.",
    textLabel: "Texte",
  },
  cover: {
    label: "Plein cadre",
    hint: "L'image occupe tout l'écran, recadrée ; le texte passe par-dessus.",
    textLabel: "Texte en surimpression",
  },
  compare: {
    label: "Comparaison",
    hint: "Deux ou trois visuels côte à côte, chacun légendé : avant/après, deux scénarios.",
    textLabel: "Phrase d'introduction",
  },
};

export function contextLayoutLabel(layout) {
  return CONTEXT_LAYOUT_INFO[layout]?.label || layout;
}

export function slotsOf(layout) {
  return CONTEXT_LAYOUT_SLOTS[layout] || CONTEXT_LAYOUT_SLOTS[DEFAULT_CONTEXT_LAYOUT];
}

/* ─── Nature du média ──────────────────────────────────────────────── */

const VIDEO_HOSTS = /(youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com)/i;
const VIDEO_FILES = /\.(mp4|webm|ogv|ogg|mov|m4v)(\?|#|$)/i;
// Cloudinary range ses vidéos sous un chemin distinct de ses images.
const CLOUDINARY_VIDEO = /\/video\/upload\//i;

export function isYoutubeUrl(url) {
  return /(youtube\.com|youtu\.be)/i.test(url || "");
}

/**
 * Photo ou vidéo ? DÉDUIT DE L'URL, et non demandé.
 *
 * L'ancienne liste déroulante « type de média » posait à l'auteur une question
 * dont le fichier qu'il venait de déposer contenait déjà la réponse — et lui
 * laissait la possibilité de répondre à côté, ce qui donnait une balise `<img>`
 * sur un mp4, ou l'inverse.
 *
 * Le type ENREGISTRÉ prime quand il existe : les 187 pages écrites avant cette
 * déduction le portent toutes, et une URL exotique déjà classée par un humain
 * n'a pas à être reclassée par une expression régulière.
 */
export function mediaTypeOf(url, stored) {
  if (stored === "video" || stored === "photo") return stored;
  if (!url) return "photo";
  if (VIDEO_HOSTS.test(url) || VIDEO_FILES.test(url) || CLOUDINARY_VIDEO.test(url)) return "video";
  return "photo";
}

/** La déduction seule, sans mémoire — pour reclasser une URL qu'on vient de changer. */
export function detectMediaType(url) {
  return mediaTypeOf(url, null);
}

/* ─── Lecture des documents existants ──────────────────────────────── */

/**
 * Le format d'une page écrite avant les formats. Les trois drapeaux ne
 * décrivaient qu'indirectement une forme ; on la retrouve ici, une fois, plutôt
 * que de la redeviner à chaque endroit qui affiche une page de contexte.
 */
function layoutFromLegacy(content) {
  if (content.only_media) return "slide";
  if (!content.media_url) return "text";
  return "split";
}

function normalizeItems(items, max) {
  return (Array.isArray(items) ? items : [])
    .slice(0, max)
    .map((item) => ({
      media_url: item?.media_url || "",
      media_type: mediaTypeOf(item?.media_url, item?.media_type),
      caption: item?.caption || "",
    }));
}

/**
 * LE CONTENU TEL QU'IL SE JOUE — canonique, sans drapeau, taillé au format.
 *
 * Un seul passage, appelé par tout ce qui affiche ou résume une page de
 * contexte : l'écran de jeu, la vignette du déroulé, le repère cartographique,
 * l'inspecteur. Les documents de la base n'ont donc pas à être migrés, et une
 * page reste lisible qu'elle ait été écrite avant ou après les formats.
 */
export function normalizeContextContent(raw) {
  const content = raw || {};
  const layout = CONTEXT_LAYOUT_IDS.includes(content.layout)
    ? content.layout
    : layoutFromLegacy(content);
  const slots = slotsOf(layout);

  // LES DRAPEAUX D'AUTREFOIS, LUS UNE DERNIÈRE FOIS. `is_text: false` sur une
  // page qui porte pourtant un texte veut dire « ne le montre pas » : c'est une
  // décision d'auteur, et l'honorer est la seule façon de changer de modèle
  // sans faire réapparaître en séance du contenu que quelqu'un avait éteint.
  const text = content.is_text === false ? "" : content.text || "";
  const audioUrl = content.is_audio === false ? "" : content.audio_url || "";

  return {
    layout,
    media_url: slots.media ? content.media_url || "" : "",
    media_type: slots.media ? mediaTypeOf(content.media_url, content.media_type) : "photo",
    text: slots.text ? text : "",
    audio_url: slots.audio ? audioUrl : "",
    items: slots.items ? normalizeItems(content.items, slots.items) : [],
    background_image_url: content.background_image_url || "",
  };
}

/**
 * Les clés que le modèle d'avant employait et que celui-ci n'emploie plus.
 * `undefined` plutôt que `false` : `JSON.stringify` les omet, donc la clé
 * DISPARAÎT du document au lieu d'y rester en disant le contraire du reste.
 *
 * `is_decision` et `decision_list` traînent sur 146 pages sans qu'aucun
 * composant ne les lise — une page de contexte n'a jamais porté de décision.
 */
export const CLEARED_LEGACY_KEYS = {
  only_media: undefined,
  is_text: undefined,
  is_audio: undefined,
  is_decision: undefined,
  decision_list: undefined,
};

/**
 * Le contenu réécrit pour un autre format : ON GARDE CE QUI SE TRANSPOSE.
 *
 * Passer de « média et panneau » à « diapo » ne doit pas faire perdre l'image,
 * et revenir en arrière ne doit pas faire perdre le texte. Ce qui n'a pas
 * d'emplacement dans le format d'arrivée est donc vidé — pas effacé du
 * brouillon en cours, mais absent du document enregistré — et l'auteur voit
 * immédiatement dans l'aperçu ce que sa page est devenue.
 */
export function contentForLayout(rawContent, layout) {
  const raw = rawContent || {};
  const slots = slotsOf(layout);

  /**
   * TOUT CE QUE LA PAGE PORTE, y compris ce que son format actuel n'affiche
   * pas. C'est la différence avec la lecture : `normalizeContextContent` REND
   * une page, donc il tait ce que la forme choisie ne montre pas ; ici on
   * DÉPLACE une page d'une forme à une autre, et ce qui n'était que caché doit
   * revenir. Sans cela, passer par « diapo » pour revenir à « média et
   * panneau » perdrait le texte en chemin, sans que rien ne le signale.
   *
   * Seul ce qu'un ancien drapeau éteignait explicitement reste éteint : le
   * modèle actuel n'a plus d'interrupteur, donc rallumer d'office un contenu
   * que quelqu'un avait tu ne se rattraperait qu'en l'effaçant.
   */
  const carried = {
    media_url: raw.media_url || "",
    media_type: mediaTypeOf(raw.media_url, raw.media_type),
    text: raw.is_text === false ? "" : raw.text || "",
    audio_url: raw.is_audio === false ? "" : raw.audio_url || "",
  };

  const items = normalizeItems(raw.items, CONTEXT_LAYOUT_SLOTS.compare.items);
  if (slots.items) {
    // Une comparaison qui vient d'un format à média unique démarre avec ce
    // média : c'est le premier des deux termes, et le plus souvent le bon.
    if (items.length === 0 && carried.media_url) {
      items.push({ media_url: carried.media_url, media_type: carried.media_type, caption: "" });
    }
    while (items.length < COMPARE_MIN_ITEMS) {
      items.push({ media_url: "", media_type: "photo", caption: "" });
    }
  }

  return {
    ...CLEARED_LEGACY_KEYS,
    layout,
    ...carried,
    // Déjà plafonnés par `normalizeItems` : les visuels d'une comparaison
    // survivent eux aussi à un aller-retour par un autre format.
    items,
    background_image_url: raw.background_image_url || "",
  };
}

/**
 * Ce que la vignette du déroulé dit d'une page de contexte en une ligne : sa
 * forme, puis ce qu'elle raconte. La forme d'abord parce que c'est elle qu'on
 * cherche des yeux en parcourant une pile de quarante écrans.
 */
export function contextSummary(rawContent) {
  const content = normalizeContextContent(rawContent);
  const label = contextLayoutLabel(content.layout);

  if (content.layout === "compare") {
    const filled = content.items.filter((item) => item.media_url).length;
    return `${label} · ${filled} visuel${filled > 1 ? "s" : ""}`;
  }
  if (content.text) return `${label} · ${content.text.slice(0, 48)}`;
  if (content.media_url) return `${label} · ${content.media_type === "video" ? "vidéo" : "image"}`;
  if (content.audio_url) return `${label} · audio`;
  return `${label} · vide`;
}

/**
 * Ce qui manque pour que la page tienne debout, en une phrase ou rien. Sert à
 * l'inspecteur (avertissement en place) et au canevas (cadre vide explicite) :
 * un emplacement non rempli ne doit pas se découvrir en séance.
 */
export function contextGaps(rawContent) {
  const content = normalizeContextContent(rawContent);
  const slots = slotsOf(content.layout);
  const gaps = [];

  if (slots.media && !content.media_url) gaps.push("aucun média n'est déposé");
  if (slots.media === "photo" && content.media_type === "video") {
    gaps.push("le média est une vidéo, or ce format demande une image");
  }
  if (content.layout === "text" && !content.text && !content.audio_url) {
    gaps.push("ni texte ni son");
  }
  if (content.layout === "cover" && !content.text) gaps.push("aucun texte en surimpression");
  if (slots.items) {
    const filled = content.items.filter((item) => item.media_url).length;
    if (filled < COMPARE_MIN_ITEMS) gaps.push("moins de deux visuels à comparer");
  }
  return gaps;
}
